/**
 * Path constants and parsers. The route table lives in App.tsx; these strings
 * are what the table, the static-page list, and the meta tags agree on.
 *
 * santa-frontend/express.js serves the v2 shell by default on .ru and .com,
 * so these paths resolve in production and on PR previews the same way they
 * do under `vite dev`.
 */
export const UI_KIT_PATH = '/ui-kit';
export const LOGIN_PATH = '/login';
export const REGISTER_PATH = '/register';
export const RECOVER_PATH = '/recover';
export const TERMS_PATH = '/terms';
export const POLICY_PATH = '/policy';
export const PRIVACY_POLICY_PATH = '/privacy_policy';
export const FAQ_PATH = '/faq';
export const PREMIUM_PATH = '/premium';
export const RANDOMIZER_PATH = '/randomizer';
export const ADMIN_PATH = '/admin';
export const ACCOUNT_PATH = '/account';
export const ACCOUNT_PROFILES_PATH = '/account/profiles';
export const ACCOUNT_BOXES_PATH = '/account/boxes';

/** Landing paths from santa-frontend — keep these strings identical. */
export const SECRET_SANTA_FOR_FRIENDS_PATH = '/secret-santa-for-friends';
export const SECRET_SANTA_FOR_COWORKERS_PATH = '/secret-santa-for-coworkers';
export const SECRET_SANTA_FOR_FAMILY_PATH = '/secret-santa-for-family';
export const SECRET_SANTA_FOR_CLASSROOM_PATH = '/secret-santa-for-classroom';
export const WHITE_ELEPHANT_PATH = '/white-elephant';

export function normalizePath(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

/**
 * A game and the ways into it. The shapes are santa-frontend's, because links
 * already in the wild point at them.
 *
 *   /box/:key
 *   /box/:key/settings
 *   /box/:key/exchange
 *   /box/:key/ward
 *   /box/:key/messenger
 *   /box/:key/card
 *   /box/:key/card/:cardKey
 *   /box/:key/card/:cardKey/edit
 */
export const BOX_PATH = '/box';
/** v1 create-game wizard. Not a box key — the router must not treat it as one. */
export const BOX_NEW_PATH = `${BOX_PATH}/new`;

export type BoxView =
  | 'main'
  | 'organizer'
  | 'organizer_panel'
  | 'organizer_legacy'
  | 'home'
  | 'settings'
  | 'exchange'
  | 'ward'
  | 'messenger'
  | 'card'
  | 'card-edit';

export interface BoxRoute {
  key: string;
  /** Present on a personal link. It is the card's randomKey, not its id. */
  cardKey?: string;
  /** True for the /card shapes: the visitor is here as a participant. */
  card: boolean;
  view: BoxView;
}

/** v1 route pattern: `[0-9a-zA-Z_\-&]+`. `/box/new` is the wizard, not a box. */
const BOX_KEY = /^[0-9a-zA-Z_\-&]+$/;
const RESERVED_BOX_KEYS = ['new'];

const ALIAS_VIEW: Record<string, BoxView> = {
  gifted: 'home',
  main: 'main',
  organizer: 'organizer',
  organizer_panel: 'organizer_panel',
  organizer_legacy: 'organizer_legacy',
  settings: 'settings',
  exchange: 'exchange',
  ward: 'ward',
  messenger: 'messenger',
  card: 'card',
  participants: 'home',
  invite: 'home',
  draw: 'home',
  santas: 'home',
  delete: 'settings',
  contact_organizer: 'messenger',
  'my-card': 'exchange',
};

export function parseBoxPath(pathname: string = window.location.pathname): BoxRoute | null {
  const segments = normalizePath(pathname).split('/').filter(Boolean);
  if (segments[0] !== 'box' || segments.length < 2 || segments.length > 5) {
    return null;
  }
  const [, key, third, fourth, fifth] = segments;
  if (!BOX_KEY.test(key) || RESERVED_BOX_KEYS.includes(key)) {
    return null;
  }
  if (segments.length === 2) {
    return { key, card: false, view: 'home' };
  }
  if (third === 'card') {
    if (segments.length === 3) {
      return { key, card: true, view: 'card' };
    }
    if (fourth === 'edit' && segments.length === 4) {
      return { key, card: true, view: 'card-edit' };
    }
    if (!BOX_KEY.test(fourth)) {
      return null;
    }
    if (segments.length === 4) {
      return { key, cardKey: fourth, card: true, view: 'card' };
    }
    if (fifth === 'edit') {
      return { key, cardKey: fourth, card: true, view: 'card-edit' };
    }
    return null;
  }
  const view = ALIAS_VIEW[third];
  if (!view || segments.length > 3) {
    return null;
  }
  return { key, card: false, view };
}

/** Short paths are scoped by the verified game hostname. */
export function parseGamePath(pathname: string, key?: string): BoxRoute | null {
  const legacy = parseBoxPath(pathname);
  if (legacy || !key || pathname.startsWith('/box/')) return legacy;
  return parseBoxPath(`/box/${key}${pathname === '/' ? '' : pathname}`);
}

export function shortGamePath(pathname: string, key: string): string {
  const prefix = `/box/${key}`;
  return pathname === prefix ? '/' : pathname.startsWith(`${prefix}/`) ? pathname.slice(prefix.length) : pathname;
}

function boxPrefix(key: string): string {
  return typeof window !== 'undefined' && window.__ssBoxKey === key ? '' : `${BOX_PATH}/${key}`;
}

export function boxPath(key: string): string {
  return boxPrefix(key) || '/';
}

export function boxCardPath(key: string, cardKey?: string): string {
  return cardKey ? `${boxPrefix(key)}/card/${cardKey}` : `${boxPrefix(key)}/card`;
}

export function boxCardEditPath(key: string, cardKey?: string): string {
  return cardKey ? `${boxPrefix(key)}/card/${cardKey}/edit` : `${boxPrefix(key)}/card/edit`;
}

export function boxSettingsPath(key: string): string {
  return `${boxPrefix(key)}/organizer?tab=settings&settingsTab=questionnaires`;
}

export function boxExchangePath(key: string): string {
  return `${boxPrefix(key)}/exchange`;
}

export function boxWardPath(key: string): string {
  return `${boxPrefix(key)}/ward`;
}

export function boxMessengerPath(key: string, selected?: string): string {
  const path = `${boxPrefix(key)}/messenger`;
  return selected ? `${path}?selected=${encodeURIComponent(selected)}` : path;
}

export const LANDING_PATHS = [
  SECRET_SANTA_FOR_FRIENDS_PATH,
  SECRET_SANTA_FOR_COWORKERS_PATH,
  SECRET_SANTA_FOR_FAMILY_PATH,
  SECRET_SANTA_FOR_CLASSROOM_PATH,
  WHITE_ELEPHANT_PATH,
] as const;

export function boxOrganizerPath(key: string): string {
  return `${boxPrefix(key)}/organizer`;
}

export function boxMainPath(key: string): string {
  return boxPath(key);
}

export function boxOrganizerPanelPath(key: string): string {
  return boxOrganizerPath(key);
}
